import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { TaskPriority, TaskStatus } from '@ovrsee/shared'
import { useAuth } from '../../lib/auth.js'

export interface TaskListItem {
  readonly id: string
  readonly reference: string
  readonly title: string
  readonly description?: string
  readonly category: string
  readonly priority: TaskPriority
  readonly complexity: number
  readonly status: TaskStatus
  readonly assigneeId: string
  readonly assignee: { readonly id: string; readonly name: string } | null
  readonly deadlineAt: string
  readonly effectiveDeadlineAt: string
  readonly assignedAt: string
  readonly closedAt?: string | null
  /** Derived server-side from @ovrsee/shared. Never recomputed here. */
  readonly isOverdue: boolean
  readonly dueIn: string
  readonly submissions?: readonly SubmissionItem[]
}

export interface TaskListPage {
  readonly items: readonly TaskListItem[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
}

export interface TaskQuery {
  readonly status?: string
  readonly q?: string
  readonly assigneeId?: string
  readonly departmentId?: string
  readonly priority?: string
  readonly complexityMin?: number
  readonly complexityMax?: number
  readonly from?: string
  readonly to?: string
  readonly page?: number
}

function parseOptionalNumber(value: string | null): number | undefined {
  if (value === null || value === '') return undefined
  const parsed = Number(value)
  return Number.isInteger(parsed) ? parsed : undefined
}

export function taskQueryFromSearchParams(params: URLSearchParams): TaskQuery {
  return {
    status: params.get('status') ?? undefined,
    q: params.get('q') ?? undefined,
    assigneeId: params.get('assigneeId') ?? undefined,
    departmentId: params.get('departmentId') ?? undefined,
    priority: params.get('priority') ?? undefined,
    complexityMin: parseOptionalNumber(params.get('complexityMin')),
    complexityMax: parseOptionalNumber(params.get('complexityMax')),
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    page: parseOptionalNumber(params.get('page')),
  }
}

export function taskQueryString(query: TaskQuery): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue
    params.set(key, String(value))
  }
  const encoded = params.toString()
  return encoded === '' ? '' : `?${encoded}`
}

export function useTasks(query: TaskQuery): UseQueryResult<TaskListPage> {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['tasks', query],
    queryFn: () => api.get<TaskListPage>(`/api/tasks${taskQueryString(query)}`),
    retry: false,
  })
}

/** Keeps a fast typist from firing a request per keystroke. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

export interface AssignablePerson {
  readonly id: string
  readonly name: string
  readonly role: string
  readonly isActive: boolean
  readonly departmentId: string | null
}

export function useAssignableUsers() {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['users', 'assignable'],
    queryFn: async () => {
      const people = await api.get<AssignablePerson[]>('/api/users')
      // A deactivated person cannot be assigned to — the API refuses it, so offering them
      // would only produce a confusing 400.
      return people.filter((person) => person.isActive)
    },
  })
}

export interface Workload {
  readonly assigneeId: string
  readonly activeCount: number
  readonly dueNext24h: number
  readonly dueNext7d: number
  readonly blocked: number
  readonly leave: {
    readonly id: string
    readonly type: 'ANNUAL' | 'SICK' | 'UNPAID' | 'OTHER'
    readonly startsAt: string
    readonly endsAt: string
    readonly current: boolean
  } | null
}

export function useWorkload(assigneeId: string | null) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['workload', assigneeId],
    // Not fetched until a person is chosen: there is nothing to preview before then.
    enabled: assigneeId !== null && assigneeId !== '',
    queryFn: () => api.get<Workload>(`/api/tasks/workload?assigneeId=${encodeURIComponent(assigneeId!)}`),
  })
}

export interface CreateTaskPayload {
  readonly title: string
  readonly description: string
  readonly category: string
  readonly assigneeId: string
  readonly priority: string
  readonly complexity: number
  readonly deadlineAt: string
  readonly dependsOnTaskId?: string | null
}

export function useCreateTask() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateTaskPayload) => api.post<{ id: string }>('/api/tasks', payload),
    onSuccess: async () => {
      // Invalidate the whole tasks key, not one query: the new task may or may not match
      // the filter currently on screen, and the count in the summary always changes.
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
      await queryClient.invalidateQueries({ queryKey: ['workload'] })
    },
  })
}

export interface TaskEventItem {
  readonly id: string
  readonly type: string
  readonly fromStatus: string | null
  readonly toStatus: string | null
  readonly note: string | null
  readonly metadata: Record<string, unknown> | null
  readonly createdAt: string
  readonly actor: { readonly id: string; readonly name: string } | null
}

export interface SubmissionItem {
  readonly id: string
  readonly channel: 'PLATFORM' | 'EXTERNAL'
  readonly note: string | null
  readonly submittedAt: string
  readonly reviewedAt: string | null
  readonly outcome: 'APPROVED' | 'RETURNED' | null
  readonly reviewNote: string | null
  readonly submittedBy: { readonly id: string; readonly name: string } | null
}

export interface AttachmentItem {
  readonly id: string
  readonly fileName: string
  readonly contentType: string
  readonly sizeBytes: number
  readonly createdAt: string
}

export interface TaskDetail extends TaskListItem {
  readonly description: string
  readonly events?: readonly TaskEventItem[]
  readonly submissions?: readonly SubmissionItem[]
  readonly attachments?: readonly AttachmentItem[]
}

export function useTaskDetail(taskId: string | null) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['task', taskId],
    enabled: taskId !== null,
    queryFn: () => api.get<TaskDetail>(`/api/tasks/${taskId!}`),
  })
}

export interface ReviewPayload {
  readonly outcome: 'APPROVED' | 'RETURNED'
  readonly reviewNote?: string
  readonly newDeadlineAt?: string
}

export function useReviewTask(taskId: string) {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReviewPayload) => api.post(`/api/tasks/${taskId}/review`, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
}
